/* Photo Motif — before/after comparator.
   Vanilla, no dependencies, no framework.

   POINTER
   Mouse: pressing anywhere on the photo moves the separator to the pointer and
   starts dragging. Touch: the press is held undecided until the finger has
   actually moved, and then until the movement is clearly sideways rather than
   merely more sideways than not — see TOUCH_SLOP and AXIS_RATIO. A clearly
   sideways move takes over the drag; everything else is left to the browser so
   the page scrolls normally (the CSS sets touch-action:pan-y, never none). A
   touch that never moves counts as a tap and positions the separator on
   release.

   Inside a horizontal carousel the sideways swipe belongs to the carousel, not
   to us — the markup then carries .pm-comparator--handle-only and a finger on
   the photo can only tap, never drag. The handle itself still drags (it sets
   touch-action:none), and a mouse keeps the whole surface either way.

   KEYBOARD
   The handle is role="slider": Left/Right move 5%, PageUp/PageDown 10%,
   Home/End jump to the clamped ends.

   INTRO
   When a comparator first becomes fully visible it plays a short nudge, so the
   photo reads as draggable without a caption saying so. The trajectory was
   measured on a reference implementation (see inspection/INSPECTION.md):
   three 500 ms segments, rest -> rest+10 -> rest-5 -> rest, in absolute
   percentage points, each segment eased with a sinusoidal in-out — the
   canonical CSS spelling of that curve is cubic-bezier(0.37, 0, 0.63, 1), and
   (1 - cos(pi*p)) / 2 is its exact form.

   Everything on screen animates at once, in phase, as on the reference — one
   shared frame loop drives them all. Two departures from it remain, both
   required by our brief: nothing plays under prefers-reduced-motion, and any
   user input cancels the intro on the spot. */

(function () {
  'use strict';

  var MIN = 2;
  var MAX = 98;
  var STEP = 5;
  var STEP_LARGE = 10;

  var SEGMENT = 500; // ms, one of three
  var RISE = 10; // percentage points, first leg
  var FALL = 5; // percentage points, overshoot back the other way
  var CEILING = 98; // beyond this the whole trajectory flips sign
  /* HOW MUCH MOVEMENT, AND HOW SIDEWAYS, BEFORE THE DRAG IS TAKEN.
     6px with a bare dy >= dx test was too eager on a full-width comparator: a
     thumb starting a page scroll covers 6px in two frames and is almost never
     exactly vertical, so a scroll that drifted one pixel sideways was read as a
     drag and the page stopped moving under the finger. 11px is about where the
     platforms put their own pan threshold, and the ratio is the half that
     matters — the gesture has to be half again more sideways than downward
     before it is ours. Everything ambiguous stays the page's, because a scroll
     that does not scroll is a broken page, while a drag that does not drag is
     one more try. */
  var TOUCH_SLOP = 11; // px before a touch is judged horizontal or vertical
  var AXIS_RATIO = 1.5; // how much more sideways than downward before we take it

  var motionQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  function motionAllowed() {
    return !(motionQuery && motionQuery.matches);
  }

  function clamp(value) {
    return Math.max(MIN, Math.min(MAX, value));
  }

  function easeInOutSine(p) {
    return (1 - Math.cos(Math.PI * p)) / 2;
  }

  /* --------------------------------------------------------------- ticker
     Every comparator that comes into view animates, and they animate together.
     One shared frame loop drives all of them, so two tiles entering on the
     same frame stay exactly in phase rather than drifting apart. */

  var active = [];
  var ticking = false;

  function join(cmp) {
    active.push(cmp);
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(tick);
  }

  function leave(cmp) {
    var i = active.indexOf(cmp);
    if (i !== -1) active.splice(i, 1);
  }

  function tick(now) {
    /* Backwards: a comparator that finishes removes itself from the list. */
    for (var i = active.length - 1; i >= 0; i--) {
      var cmp = active[i];
      if (cmp.t0 === null) cmp.t0 = now;
      var t = now - cmp.t0;

      if (t >= SEGMENT * 3) {
        cmp.position = cmp.rest;
        cmp.endIntro();
      } else {
        cmp.position = cmp.introAt(t);
      }
      cmp.paint();
    }

    if (active.length) requestAnimationFrame(tick);
    else ticking = false;
  }

  /* --------------------------------------------------------------- observer */

  var observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(onIntersect, { threshold: [0, 0.5, 0.9, 0.99, 1] })
      : null;

  function onIntersect(entries) {
    entries.forEach(function (entry) {
      var cmp = entry.target.pmComparator;
      if (!cmp) return;

      cmp.ratio = entry.intersectionRatio;
      cmp.showing = entry.isIntersecting;
      /* A comparator taller than the viewport can never reach a ratio of 1. */
      cmp.oversized = entry.boundingClientRect.height > window.innerHeight * 0.9;

      if (cmp.introSpent) {
        observer.unobserve(entry.target);
        return;
      }
      if (cmp.readyForIntro()) cmp.startIntro();
    });
  }

  /* ------------------------------------------------------------- component */

  function Comparator(root) {
    this.root = root;
    this.handle = root.querySelector('[data-pm-comparator-handle]');
    this.rest = clamp(parseFloat(root.getAttribute('data-start')) || 50);
    this.position = this.rest;

    this.handleOnly = root.classList.contains('pm-comparator--handle-only');

    this.pointerId = null;
    this.dragging = false;
    this.undecided = false;
    this.tapOnly = false;
    this.startX = 0;
    this.startY = 0;

    this.ratio = 0;
    this.showing = false;
    this.oversized = false;
    this.introSpent = false;
    this.introRunning = false;
    this.t0 = null;

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerCancel = this.onPointerCancel.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    root.addEventListener('pointerdown', this.onPointerDown);
    root.addEventListener('pointermove', this.onPointerMove);
    root.addEventListener('pointerup', this.onPointerUp);
    root.addEventListener('pointercancel', this.onPointerCancel);
    if (this.handle) this.handle.addEventListener('keydown', this.onKeyDown);

    root.pmComparator = this;
    this.paint();

    if (observer && motionAllowed()) observer.observe(root);
    else this.introSpent = true;
  }

  Comparator.prototype.readyForIntro = function () {
    if (!this.showing) return false;
    return this.ratio >= 0.99 || (this.oversized && this.ratio > 0.6);
  };

  /* Writes the position. aria is left alone while the intro runs — a slider
     announcing sixty values in a second is useless to a screen reader. */
  Comparator.prototype.paint = function () {
    this.root.style.setProperty('--pm-cmp-pos', this.position + '%');
    if (!this.introRunning) this.announce();
  };

  Comparator.prototype.announce = function () {
    if (!this.handle) return;
    var value = Math.round(this.position);
    this.handle.setAttribute('aria-valuenow', String(value));
    this.handle.setAttribute('aria-valuetext', value + '%');
  };

  /* ------------------------------------------------------------------ intro */

  Comparator.prototype.introAt = function (t) {
    /* Flip the whole trajectory rather than clamping or shrinking it when the
       rest position sits too close to the right edge: same measured amplitude,
       opposite direction. */
    var way = this.rest + RISE > CEILING ? -1 : 1;
    var peak = this.rest + way * RISE;
    var trough = this.rest - way * FALL;

    if (t <= SEGMENT) {
      return this.rest + (peak - this.rest) * easeInOutSine(t / SEGMENT);
    }
    if (t <= SEGMENT * 2) {
      return peak + (trough - peak) * easeInOutSine((t - SEGMENT) / SEGMENT);
    }
    return trough + (this.rest - trough) * easeInOutSine((t - SEGMENT * 2) / SEGMENT);
  };

  Comparator.prototype.startIntro = function () {
    if (this.introSpent) return;
    this.introSpent = true;
    if (!motionAllowed()) return;

    this.introRunning = true;
    this.rest = this.position;
    this.t0 = null;
    join(this);
  };

  Comparator.prototype.endIntro = function () {
    this.introRunning = false;
    this.t0 = null;
    leave(this);
    this.announce();
  };

  /* Any user input wins, immediately, wherever the nudge had got to. */
  Comparator.prototype.interrupt = function () {
    this.introSpent = true;
    if (this.introRunning) this.endIntro();
  };

  /* ---------------------------------------------------------------- pointer */

  Comparator.prototype.setFromEvent = function (event) {
    var box = this.root.getBoundingClientRect();
    if (!box.width) return;
    this.position = clamp(((event.clientX - box.left) / box.width) * 100);
    this.paint();
  };

  Comparator.prototype.beginDrag = function (event) {
    this.dragging = true;
    this.undecided = false;
    if (this.root.setPointerCapture) {
      try {
        this.root.setPointerCapture(event.pointerId);
      } catch (e) {
        /* capture is a nicety, dragging still works without it */
      }
    }
  };

  Comparator.prototype.reset = function () {
    this.dragging = false;
    this.undecided = false;
    this.tapOnly = false;
    this.pointerId = null;
  };

  Comparator.prototype.onHandle = function (event) {
    return !!this.handle && (event.target === this.handle || this.handle.contains(event.target));
  };

  Comparator.prototype.onPointerDown = function (event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.interrupt();

    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startY = event.clientY;

    if (event.pointerType === 'mouse') {
      this.beginDrag(event);
      if (this.handle) this.handle.focus({ preventScroll: true });
      this.setFromEvent(event);
      event.preventDefault();
      return;
    }

    /* Touch and pen: decide on the first move, not now. Calling
       preventDefault here would kill the page scroll. */
    this.undecided = true;
    /* In a carousel, a finger on the photo may only tap: any movement is the
       carousel's, or the page's. */
    this.tapOnly = this.handleOnly && !this.onHandle(event);
  };

  Comparator.prototype.onPointerMove = function (event) {
    if (event.pointerId !== this.pointerId) return;

    if (this.undecided) {
      var dx = Math.abs(event.clientX - this.startX);
      var dy = Math.abs(event.clientY - this.startY);
      if (dx < TOUCH_SLOP && dy < TOUCH_SLOP) return;
      if (this.tapOnly || dx <= dy * AXIS_RATIO) {
        /* Vertical intent, a diagonal too close to call, or a swipe that
           belongs to the carousel: hand the gesture back. */
        this.reset();
        return;
      }
      this.beginDrag(event);
      if (this.handle) this.handle.focus({ preventScroll: true });
    }

    if (!this.dragging) return;
    this.setFromEvent(event);
    if (event.cancelable) event.preventDefault();
  };

  Comparator.prototype.onPointerUp = function (event) {
    if (event.pointerId !== this.pointerId) return;

    /* A touch that never moved is a tap: place the separator there. */
    if (this.undecided) this.setFromEvent(event);

    if (this.dragging && this.root.releasePointerCapture) {
      try {
        if (!this.root.hasPointerCapture || this.root.hasPointerCapture(event.pointerId)) {
          this.root.releasePointerCapture(event.pointerId);
        }
      } catch (e) {
        /* already released */
      }
    }
    this.reset();
  };

  Comparator.prototype.onPointerCancel = function (event) {
    if (event.pointerId !== this.pointerId) return;
    this.reset();
  };

  /* --------------------------------------------------------------- keyboard */

  Comparator.prototype.onKeyDown = function (event) {
    var next = null;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        next = this.position - STEP;
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        next = this.position + STEP;
        break;
      case 'PageDown':
        next = this.position - STEP_LARGE;
        break;
      case 'PageUp':
        next = this.position + STEP_LARGE;
        break;
      case 'Home':
        next = MIN;
        break;
      case 'End':
        next = MAX;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.interrupt();
    this.position = clamp(next);
    this.paint();
  };

  /* -------------------------------------------------------------------- init */

  function init(scope) {
    (scope || document).querySelectorAll('[data-pm-comparator]').forEach(function (root) {
      if (root.dataset.pmComparatorReady) return;
      root.dataset.pmComparatorReady = 'true';
      new Comparator(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Someone turning reduced motion on mid-visit stops everything at once. */
  if (motionQuery) {
    var onMotionChange = function () {
      if (motionAllowed()) return;
      for (var i = active.length - 1; i >= 0; i--) {
        var cmp = active[i];
        cmp.position = cmp.rest;
        cmp.endIntro();
        cmp.paint();
      }
    };
    if (motionQuery.addEventListener) motionQuery.addEventListener('change', onMotionChange);
    else if (motionQuery.addListener) motionQuery.addListener(onMotionChange);
  }

  /* Theme editor: sections are re-rendered without a page load. */
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
